import React, { useEffect, useState } from 'react';
import axios from 'axios';
import API_BASE_URL from '../../components/apiconfig/api-config';
import { FaFilter, FaBoxOpen, FaTruckLoading, FaChartPie, FaRupeeSign, FaShippingFast, FaCheckCircle, FaUserTie, FaBuilding } from 'react-icons/fa';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import NoRecordFound from '../../components/common/no-record-found';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

const formatNumber = (num) => Number(num || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const formatCurrency = (num) => '₹' + Number(num || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

const Dashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showDateFilters, setShowDateFilters] = useState(false);

  const fetchData = async (start = null, end = null) => {
    try {
      setLoading(true);
      const params = {};
      if (start) params.startDate = start;
      if (end) params.endDate = end;

      const response = await axios.get(`${API_BASE_URL}/api/dashboard`, { params, withCredentials: true });
      setData(response.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleDateFilter = () => {
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      alert("End date cannot be before start date");
      return;
    }
    fetchData(startDate, endDate);
  };

  const handleResetDateFilter = () => {
    setStartDate(''); setEndDate(''); fetchData();
  };

  if (loading || !data) return <div className="p-8 text-center text-gray-500 font-medium">Loading premium dashboard...</div>;

  const { pipeline, topSuppliers, topBuyers, commodities, financials } = data;

  const doughnutData = {
    labels: commodities.map(item => item.commodity_name),
    datasets: [{
      data: commodities.map(item => item.remaining_qty),
      backgroundColor: ['#10b981', '#3b82f6', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4'],
      borderWidth: 0,
    }],
  };

  const barData = {
    labels: commodities.map(item => item.commodity_name),
    datasets: [
      { label: 'Imported (MT)', data: commodities.map(item => item.total_import_qty), backgroundColor: '#93c5fd', borderRadius: 4 },
      { label: 'Sold (MT)', data: commodities.map(item => item.total_sales_qty), backgroundColor: '#10b981', borderRadius: 4 }
    ],
  };

  return (
    <div className="flex flex-col h-full space-y-4 pb-8 bg-white font-sans">
      {/* Top Navigation & Filters */}
      <div className="bg-white border-b border-gray-200 p-4 flex flex-col sm:flex-row justify-between items-center gap-3 shadow-sm">
        <h2 className="text-lg font-bold text-gray-800 tracking-tight">Business Overview</h2>
        <div className="flex items-center gap-3">
          <button onClick={() => setShowDateFilters(!showDateFilters)} className="flex items-center gap-2 px-3 py-1.5 bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 rounded-md shadow-sm text-sm font-medium transition-all">
            <FaFilter className="text-gray-400" /> {showDateFilters ? 'Hide Filters' : 'Filter Data'}
          </button>
        </div>
      </div>

      {showDateFilters && (
        <div className="mx-2 bg-white p-3 rounded-md shadow-sm border border-gray-200 flex flex-wrap items-end gap-3 animate-in slide-in-from-top-2">
          <div>
            <label className="block text-xs font-bold text-gray-500 mb-1 uppercase tracking-wider">From Date</label>
            <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="px-3 py-1.5 border border-gray-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-blue-500 transition-all" />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 mb-1 uppercase tracking-wider">To Date</label>
            <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="px-3 py-1.5 border border-gray-300 rounded-md text-sm outline-none focus:ring-2 focus:ring-blue-500 transition-all" />
          </div>
          <button onClick={handleDateFilter} className="bg-blue-600 text-white px-4 py-1.5 rounded-md text-sm font-semibold hover:bg-blue-700 shadow-sm transition-all active:scale-95">Apply</button>
          <button onClick={handleResetDateFilter} className="bg-gray-100 text-gray-600 px-4 py-1.5 rounded-md text-sm font-semibold hover:bg-gray-200 border border-gray-300 transition-all active:scale-95">Reset</button>
        </div>
      )}

      {/* KPI Cards */}
      <div className="px-2 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-md shadow-sm border border-gray-200 flex items-center justify-between hover:shadow-md transition-shadow">
          <div>
            <p className="text-gray-500 text-[11px] uppercase font-bold tracking-wider mb-0.5">Active Contracts</p>
            <h3 className="text-xl font-bold text-gray-900">{formatNumber(pipeline.active_contracts_qty)} <span className="text-[11px] font-medium text-gray-400">MT</span></h3>
          </div>
          <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
            <FaCheckCircle className="text-lg" />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-md shadow-sm border border-gray-200 flex items-center justify-between hover:shadow-md transition-shadow">
          <div>
            <p className="text-gray-500 text-[11px] uppercase font-bold tracking-wider mb-0.5">In Transit</p>
            <h3 className="text-xl font-bold text-gray-900">{formatNumber(pipeline.in_transit_volume)} <span className="text-[11px] font-medium text-gray-400">MT</span></h3>
          </div>
          <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center text-amber-600">
            <FaShippingFast className="text-lg" />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-md shadow-sm border border-gray-200 flex items-center justify-between hover:shadow-md transition-shadow">
          <div>
            <p className="text-gray-500 text-[11px] uppercase font-bold tracking-wider mb-0.5">Warehouse Stock</p>
            <h3 className="text-xl font-bold text-gray-900">{formatNumber(pipeline.warehouse_stock)} <span className="text-[11px] font-medium text-gray-400">MT</span></h3>
          </div>
          <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600">
            <FaBoxOpen className="text-lg" />
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-md shadow-sm border border-gray-200 flex items-center justify-between hover:shadow-md transition-shadow">
          <div>
            <p className="text-gray-500 text-[11px] uppercase font-bold tracking-wider mb-0.5">Pending Dispatches</p>
            <h3 className="text-xl font-bold text-gray-900">{formatNumber(pipeline.pending_dispatches)} <span className="text-[11px] font-medium text-gray-400">MT</span></h3>
          </div>
          <div className="w-10 h-10 rounded-full bg-purple-50 flex items-center justify-center text-purple-600">
            <FaTruckLoading className="text-lg" />
          </div>
        </div>
      </div>

      {/* Financials & Top Metrics */}
      <div className="px-2 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1 bg-white rounded-md shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
          <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2"><FaRupeeSign className="text-emerald-500"/> Financial Snapshot</h3>
          <div className="space-y-3">
            <div className="p-3 bg-gray-50 rounded-md border border-gray-100">
              <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">Est. Import Value</p>
              <h4 className="text-lg font-bold text-gray-900 mt-1">{formatCurrency(financials.total_import_value)}</h4>
            </div>
            <div className="p-3 bg-gray-50 rounded-md border border-gray-100">
              <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">Est. Sales Value</p>
              <h4 className="text-lg font-bold text-gray-900 mt-1">{formatCurrency(financials.total_sales_value)}</h4>
            </div>
            <div className="p-3 bg-gray-50 rounded-md border border-gray-100">
              <p className="text-[10px] text-gray-500 font-bold uppercase tracking-wider">Projected Gross Profit</p>
              <h4 className="text-lg font-bold text-gray-900 mt-1">{formatCurrency(financials.estimated_profit)}</h4>
            </div>
          </div>
        </div>

        <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="bg-white rounded-md shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow flex flex-col">
            <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2"><FaBuilding className="text-blue-500"/> Top Suppliers (By Vol)</h3>
            <div className="space-y-3 flex-1 overflow-y-auto">
              {topSuppliers.length === 0 ? <p className="text-xs text-gray-400 italic text-center mt-5">No data</p> : topSuppliers.map((s, i) => (
                <div key={i} className="flex justify-between items-center group border-b border-gray-50 pb-2 last:border-0 last:pb-0">
                  <span className="text-xs font-semibold text-gray-700 truncate pr-2 group-hover:text-blue-600 transition-colors"><span className="text-gray-400 mr-1">{i+1}.</span>{s.name}</span>
                  <span className="text-[11px] font-bold text-gray-800 bg-gray-50 px-2 py-1 rounded-md group-hover:bg-blue-50 group-hover:text-blue-700 transition-colors">{formatNumber(s.total_volume)} MT</span>
                </div>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-md shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow flex flex-col">
            <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2"><FaUserTie className="text-amber-500"/> Top Buyers (By Vol)</h3>
            <div className="space-y-3 flex-1 overflow-y-auto">
              {topBuyers.length === 0 ? <p className="text-xs text-gray-400 italic text-center mt-5">No data</p> : topBuyers.map((b, i) => (
                <div key={i} className="flex justify-between items-center group border-b border-gray-50 pb-2 last:border-0 last:pb-0">
                  <span className="text-xs font-semibold text-gray-700 truncate pr-2 group-hover:text-amber-600 transition-colors"><span className="text-gray-400 mr-1">{i+1}.</span>{b.name}</span>
                  <span className="text-[11px] font-bold text-gray-800 bg-gray-50 px-2 py-1 rounded-md group-hover:bg-amber-50 group-hover:text-amber-700 transition-colors">{formatNumber(b.total_volume)} MT</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="px-2 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-md shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
          <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2"><FaChartPie className="text-purple-500"/> Import vs Sales (MT)</h3>
          <div className="h-56">
            {commodities.length > 0 ? <Bar data={barData} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { labels: { usePointStyle: true, boxWidth: 6, font: { size: 10 } } } } }} /> : <NoRecordFound />}
          </div>
        </div>
        <div className="bg-white rounded-md shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
          <h3 className="text-sm font-bold text-gray-800 mb-4 flex items-center gap-2"><FaBoxOpen className="text-emerald-500"/> Remaining Stock</h3>
          <div className="h-56 flex justify-center pb-2">
            {commodities.some(c => c.remaining_qty > 0) ? <Doughnut data={doughnutData} options={{ responsive: true, maintainAspectRatio: false, cutout: '70%', plugins: { legend: { position: 'bottom', labels: { usePointStyle: true, boxWidth: 6, padding: 10, font: { size: 10 } } } } }} /> : <NoRecordFound />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;